/**
 * Buffered (non-streaming) chat transport (ADR 0006 degradation path): POSTs the live pi-server chat
 * endpoints and replays the single JSON reply as the same chunk vocabulary a streamed turn produces,
 * so the adapter's `onFinishMetadata` tap and normalization serve both transports identically.
 * A `ChatboxStreamFactory`, so APP-3358 swaps the streaming factory in with no surface change.
 */

import type { AiSdkUIMessageChunk } from "@mui/x-chat"
import type { ChatMessage } from "@mui/x-chat-headless"
import { log } from "helpers/logger"
import type { ChatboxStreamFactory } from "./adapter"
import { type ArtifactEnvelope, isArtifactEnvelope } from "./artifact-contract"
import { describeChatApiError } from "./chat-api-errors"

// Mirrors the streaming transport's fixed text-part id: a buffered turn is one whole text part.
const ASSISTANT_TEXT_PART_ID = "assistant-text"

/** The pi-server thread start/continue response, as `api/aiChat.js` serializes it. */
export interface BufferedAssistantResponse {
  threadId: string
  assistantMessage: {
    id?: string
    text?: string
    artifacts: ArtifactEnvelope[]
  }
}

/** The concatenated text of a ChatBox message's `text` parts. */
export function messageText(message: Pick<ChatMessage, "parts">): string {
  return message.parts
    .filter((part): part is { type: "text", text: string } => part.type === "text" && typeof (part as { text?: unknown }).text === "string")
    .map((part) => part.text)
    .join("")
}

/** Narrow the POST response (crosses the wire as `unknown`) field-by-field; `null` if malformed. */
export function narrowAssistantResponse(value: unknown): BufferedAssistantResponse | null {
  if (typeof value !== "object" || value === null) return null
  const candidate = value as { threadId?: unknown, assistantMessage?: unknown }
  if (typeof candidate.threadId !== "string") return null

  const message = candidate.assistantMessage
  if (typeof message !== "object" || message === null) return null
  const messageFields = message as { id?: unknown, text?: unknown, artifacts?: unknown }

  return {
    threadId: candidate.threadId,
    assistantMessage: {
      id: typeof messageFields.id === "string" ? messageFields.id : undefined,
      text: typeof messageFields.text === "string" ? messageFields.text : undefined,
      artifacts: Array.isArray(messageFields.artifacts) ? messageFields.artifacts.filter(isArtifactEnvelope) : [],
    },
  }
}

/** Replay a buffered JSON response as the streaming transport's chunk vocabulary (whole answer as one text part, then finish). */
export function responseToChunks(response: BufferedAssistantResponse): AiSdkUIMessageChunk[] {
  const { assistantMessage, threadId } = response
  const text = assistantMessage.text ?? ""
  return [
    { type: "text-start", id: ASSISTANT_TEXT_PART_ID },
    { type: "text-delta", id: ASSISTANT_TEXT_PART_ID, delta: text },
    { type: "text-end", id: ASSISTANT_TEXT_PART_ID },
    {
      type: "finish",
      messageMetadata: {
        threadId,
        messageId: assistantMessage.id,
        text,
        artifacts: assistantMessage.artifacts,
      },
    },
  ]
}

/** A calm, terminal error chunk — the MUI adapter surfaces it as an inline message error. */
function errorChunk(message: string): AiSdkUIMessageChunk {
  return { type: "error", errorText: message }
}

/** POSTs the user's message and resolves the raw JSON response; the abort signal cancels the request. */
export type PostChatMessage = (userText: string, signal: AbortSignal) => Promise<unknown>

/** Build the `ChatboxStreamFactory` for the buffered path; consumer cancellation and request aborts are both honored (no enqueue-after-close). */
export function createBufferedChatStream({ postChatMessage }: { postChatMessage: PostChatMessage }): ChatboxStreamFactory {
  return (request) => {
    const userText = messageText(request.message)
    // Own the abort so a consumer cancel actually stops the in-flight POST (not just discards its result);
    // chain the adapter's request signal in so an upstream abort still propagates to the request.
    const abortController = new AbortController()
    const abort = () => abortController.abort()
    if (request.signal.aborted) abort()
    else request.signal.addEventListener("abort", abort, { once: true })
    let cancelled = false

    return new ReadableStream<AiSdkUIMessageChunk>({
      async start(controller) {
        try {
          const raw = await postChatMessage(userText, abortController.signal)
          // After consumer cancellation the controller is gone — nothing may touch it.
          if (cancelled) return
          if (abortController.signal.aborted) {
            controller.close()
            return
          }
          const response = narrowAssistantResponse(raw)
          if (response === null) {
            controller.enqueue(errorChunk("The assistant returned an unexpected response. Please try again."))
            controller.close()
            return
          }
          for (const chunk of responseToChunks(response)) {
            controller.enqueue(chunk)
          }
          controller.close()
        } catch (error) {
          if (cancelled) return
          // An aborted turn's rejection is expected teardown, not an error to render.
          if (abortController.signal.aborted) {
            controller.close()
            return
          }
          log(error)
          controller.enqueue(errorChunk(describeChatApiError(error).message))
          controller.close()
        }
      },
      cancel() {
        cancelled = true
        // Actually stop the in-flight POST, not just flag it discarded.
        abortController.abort()
      },
    })
  }
}
